import React from 'react';
import { Drawer, Form, Button, SelectPicker, Message } from 'rsuite';
import { useTranslation } from 'react-i18next';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
  registrationNumber: string;
  serviceLine?: {
    serviceLine: string;
  };
}

interface ServiceLine {
  _id: string;
  serviceLine: string;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    department: string;
    balanceStatus: string;
  };
  users: User[];
  serviceLines: ServiceLine[];
  usersLoading: boolean;
  serviceLinesLoading: boolean;
  toaster: any;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  serviceLines,
  usersLoading,
  serviceLinesLoading,
  toaster
}) => {
  const [formValue, setFormValue] = React.useState(currentFilters);

  const departmentOptions = React.useMemo(() => {
    return [
      { label: 'ALL', value: '' },
      ...serviceLines.map(sl => ({
        label: sl.serviceLine.toUpperCase(),
        value: sl.serviceLine
      }))
    ];
  }, [serviceLines]);
  const { t } = useTranslation();
  const balanceOptions = [
    { label: t('ALL'), value: '' },
    { label: t('WITH BALANCE'), value: 'WITH_BALANCE' },
    { label: t('WITHOUT BALANCE'), value: 'WITHOUT_BALANCE' }
  ];

  const userOptions = React.useMemo(() => {
    return users.map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id
    }));
  }, [users]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId,
        department: formValue.department,
        balanceStatus: formValue.balanceStatus
      });
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    setFormValue({ userId: '', department: '', balanceStatus: '' });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue(currentFilters);
    }
  }, [open, currentFilters]);

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t("Select user")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Team')}</Form.ControlLabel>
            <Form.Control
              name="department"
              accepter={SelectPicker}
              data={departmentOptions}
              loading={serviceLinesLoading}
              searchable={false}
              placeholder={t("Select team")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Leave Balance Status')}</Form.ControlLabel>
            <Form.Control
              name="balanceStatus"
              accepter={SelectPicker}
              data={balanceOptions}
              searchable={false}
              placeholder={t("Select balance status")}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
